import {getSafely} from 'shared/web';

import type {EagColumn} from '../types/EagColumn';
import type {EagColumnGroup, EagColumnOrGroup} from '../types/EagColumnGroup';
import {isEagColumnGroup} from './isEagColumnGroup';

/**
 * Appends `colDef` to `colDefsArr`, folding it into a column group when the
 * column declares a known `columnGroup`:
 * - no group (or an unknown group id) → pushed as a standalone column;
 * - same group as the current last entry → merged into that group's children,
 *   so only *consecutive* same-group columns share a group cell (this is why the
 *   caller sorts by the preset-driven column order before accumulating);
 * - otherwise → a fresh group entry is started.
 *
 * Mutates and returns `colDefsArr` in place, matching the original transform
 * loop exactly, so the caller keeps a single accumulator with no extra copies.
 */
export const addColumnToGroupedArray = (
  colDefsArr: EagColumnOrGroup[],
  colDef: EagColumn,
  columnGroup: string | null | undefined,
  groups: Record<string, EagColumnGroup> | undefined
): EagColumnOrGroup[] => {
  if (!columnGroup || !groups) {
    colDefsArr.push(colDef);
    return colDefsArr;
  }

  const group = getSafely(groups, columnGroup);

  if (!group) {
    colDefsArr.push(colDef);
    return colDefsArr;
  }

  // Stamp the group's display label onto the column itself: the grouped structure is
  // flattened away by flattenColumnConfig, and clipboard export (WITH_HEADER_GROUPS)
  // reads the label back from EagColumn.group.
  const groupedColDef: EagColumn = {...colDef, group: group.headerName};

  const lastItemIndex = colDefsArr.length - 1;
  const lastItem = colDefsArr[lastItemIndex];

  if (lastItem && isEagColumnGroup(lastItem) && lastItem.groupId === columnGroup) {
    colDefsArr[lastItemIndex] = {
      ...lastItem,
      children: [...lastItem.children, groupedColDef],
    };
  } else {
    colDefsArr.push({
      ...group,
      children: [groupedColDef],
    });
  }

  return colDefsArr;
};
